import * as React from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { draftsPath } from "../BrouillonsV2/paths";
import { ENV_VERIFIED_TRAINER, getActiveBrandConfig, getActivePrestationBrandConfig, isBrandActive } from "../../config/environment.config";
import Layout from "../../Components/Layout";
import HomeHeader, { type HomeTabId } from "../../Components/Ui/HomeHeader";
import useHome, { LEVEL_CONFIG } from "../../Hooks/Dashboard/useHome";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import OnboardingTab from "../../Components/App/Home/OnboardingTab";
import AllBordTab from "../../Components/App/Home/AllBordTab";
import BrandNavHeader from "../../Components/shared/BrandNavHeader";
// Teachr_Repository et userTeachrSetAccountAction - réintégrer à la prochaine version (système de niveaux)
import { AppDispatch } from "../../Redux/store";
import { DispatchClientParams } from "../../config/CLIENT_TYPE_CONFIG";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import TeachrRepository from "../../Repositories/TeachrRepository";
import SponsorshipRepository from "../../Repositories/SponsorshipRepository";
import { DashboardProvider } from "../../Contexts/DashboardContext";
import IndexApp from "../../Components/IndexApp";
import AddStudentDialogTrigger, { AddStudentDialogHandle } from "../../Components/shared/AddStudentDialogTrigger";
import TeachrInfoDialog, { TeachrInfoDialogHandle } from "../../Components/shared/TeachrInfoDialog";
import PartnerAdviceBanner from "../../Components/shared/PartnerAdviceBanner";
import { GET_AMOUNT_DURATION, GET_INFO_CHART, GET_NEXT_COURSE, GET_PENDING_COURSE, getSubject } from "../../Redux/Actions/PunctualCourseActions";
import { GET_ALL_CLIENTS } from "../../Redux/Actions/StudentActions";
import { GET_SPONSORSHIP_BY_TEACHR } from "../../Redux/Actions/SponsorshipActions";


const TeachrHome = () => {
    const navigate = useNavigate();
    const { classes } = useTailwindBrand();
    const teachr = useSelector((state: any) => state.user?.currentUser?.teachr);
    const dispatch = useDispatch<AppDispatch>();
    const [type, setType] = useState('6month');
    const infoDialogRef = React.useRef<TeachrInfoDialogHandle>(null);
    const studentDialogRef = React.useRef<AddStudentDialogHandle>(null);
    const hasShownInfos = teachr?.isShowInfos;
    const teachrId: number | undefined = teachr?.id;

    const startTeachr = React.useMemo(() => {
        if (teachr?.createdAt) {
            const date = new Date(teachr.createdAt);
            return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        }
        return '';
    }, [teachr]);

    const dataDispatch = React.useMemo(() => ({
        currentPage: 0,
        isData: false,
        type,
        setType,
        startTeachr,
        global: { teachr, nameSearch: '', sort: { var: 0, sort: 1 } },
    }), [type, teachr, startTeachr]);

    const dataObject: DispatchClientParams = React.useMemo(() => ({
        teachrId: teachrId as number,
        archive: 0,
        page: 0,
        variable: 0,
        sort: 1,
        limit: 10,
        validated: 1,
        search: "",
    }), [teachrId]);

    const { data: pendingCourseData, isLoading: isLoadingPendingCourse } = useQuery({
        queryKey: ['pendingCourse', teachrId],
        queryFn: () => PunctualCourseRepository.getPendingPunctualCourseByTeachr(teachrId!, 0, 10),
        enabled: !!teachrId,
    });
    const { data: getCourseNextMonthData, isLoading: isLoadingGetCourseNextMonth } = useQuery({
        queryKey: ['getCourseNextMonth', teachrId],
        queryFn: () => PunctualCourseRepository.getCourseNextMonth(teachrId!, 0, 10),
        enabled: !!teachrId,
    });

    const { data: infoChartData, isLoading: isLoadingInfoChart } = useQuery({
        queryKey: ['infoChart', teachrId, type],
        queryFn: () => PunctualCourseRepository.retreivePaidAmountInChart(teachrId!, type),
        enabled: !!teachrId,
    });

    const { data: clientsData, isLoading: isLoadingClients } = useQuery({
        queryKey: ['allClients', teachrId],
        queryFn: () => TeachrRepository.getAllClientsForTeachr(dataObject),
        enabled: !!teachrId,
    });

    const { data: sponsorshipData, isLoading: isLoadingSponsorship } = useQuery({
        queryKey: ['sponsorship', teachrId],
        queryFn: () => SponsorshipRepository.getSponsorshipByTeachr({ teachr, page: 0, limit: 10 }),
        enabled: !!teachrId,
    });

    const { data: amountAndDurationData, isLoading: isLoadingAmountDuration } = useQuery({
        queryKey: ['amountAndDuration', teachrId],
        queryFn: () => PunctualCourseRepository.retreivePaidAmountInChart(teachrId!, 'startTeach'),
        enabled: !!teachrId,
    });

    const isLoading = isLoadingPendingCourse || isLoadingInfoChart || isLoadingClients || isLoadingSponsorship || isLoadingAmountDuration || isLoadingGetCourseNextMonth;

    React.useEffect(() => {
        dispatch({ type: GET_PENDING_COURSE, payload: pendingCourseData });
        dispatch({ type: GET_NEXT_COURSE, payload: getCourseNextMonthData });
        dispatch({ type: GET_INFO_CHART, payload: infoChartData });
        dispatch({ type: GET_ALL_CLIENTS, payload: clientsData });
        dispatch({ type: GET_SPONSORSHIP_BY_TEACHR, payload: sponsorshipData });
        dispatch({ type: GET_AMOUNT_DURATION, payload: amountAndDurationData });
        dispatch(getSubject(teachr?.id))
    }, [amountAndDurationData, clientsData, dispatch, getCourseNextMonthData, infoChartData, pendingCourseData, sponsorshipData, teachr?.id])

    const course = pendingCourseData?.courses;
    const upcomingCourses: number = pendingCourseData?.totalItems ?? 0;
    const totalStudents: number = clientsData?.totalValidated ?? 0;
    const totalStudentsNoActive: number = clientsData?.totalNotValidated ?? 0;
    const sponsorship = React.useMemo(() => sponsorshipData ?? {}, [sponsorshipData]);
    const coursesNext = getCourseNextMonthData ? getCourseNextMonthData?.courses : [];

    const handleAddUser = React.useCallback(() => {
        if (studentDialogRef.current) studentDialogRef.current?.open()
    }, [])

    const handleDeclare = React.useCallback(() => {
        if (!hasShownInfos) {
            return infoDialogRef.current?.open();
        }
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}/declarer`);
    }, [hasShownInfos, navigate])

    const {
        brandColors,
        getSponsorshipMetrics,
        buildSponsorshipCards,
        getOnboardingSteps,
    } = useHome();

    const { steps: onboardingSteps, overallProgress: onboardingProgress, currentLevelNum } =
        React.useMemo(() => getOnboardingSteps(handleAddUser, handleDeclare, teachr, totalStudents, totalStudentsNoActive),
            [getOnboardingSteps, handleAddUser, handleDeclare, teachr, totalStudents, totalStudentsNoActive]
        );

    const sponsorshipMetrics = React.useMemo(() => getSponsorshipMetrics(sponsorship), [sponsorship, getSponsorshipMetrics]);

    const sponsorshipCards = React.useMemo(
        () => buildSponsorshipCards(sponsorshipMetrics, classes.primaryText),
        [sponsorshipMetrics, classes.primaryText, buildSponsorshipCards]
    );

    // handleNextLevel et nextLevelInfo - réintégrer à la prochaine version (système de niveaux)

    const currentLevelInfo = LEVEL_CONFIG[currentLevelNum] ?? LEVEL_CONFIG[1];

    const [activeTab, setActiveTab] = React.useState<HomeTabId>("start");

    React.useEffect(() => {
        if (onboardingProgress === 100 && activeTab === "start") {
            setActiveTab("all-bord");
        }
    }, [onboardingProgress, activeTab]);

    const handleNavigateToSponsorship = () => {
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, {
            state: { step: 3 },
        });
    };

    const changeStart = () => setActiveTab("start");
    const changeAllBord = () => setActiveTab("all-bord");

    const notification = {
        title: 'Taux de complétion',
        label: `${onboardingProgress}% complétés`
    }


    return (
        <DashboardProvider value={{ dataDispatch }}>
            <AddStudentDialogTrigger
                ref={studentDialogRef}
                teachr={teachr}
            // params={dataObject}
            />
            <TeachrInfoDialog
                ref={infoDialogRef}
                teachr={teachr}
            />
            <IndexApp
                index={0}
                component={
                    <Layout
                        header={
                            <HomeHeader
                                teachr={teachr}
                                title="📊 Tableau de bord"
                                notification={isBrandActive(ENV_VERIFIED_TRAINER) ? undefined : notification}
                            // currentLevel désactivé - réintégrer à la prochaine version
                            >
                                <div className="border-b border-gray-200 pb-2">
                                    <BrandNavHeader
                                        component={activeTab === "start" ? 7 : 11}
                                        currentPage={0}
                                        isHere={true}
                                        changeStart={changeStart}
                                        changeAllBord={changeAllBord}
                                        overallProgress={onboardingProgress}
                                        hasNextCourse={coursesNext?.length > 0}
                                        isLoading={isLoading}
                                        handleNextCourse={() => {
                                            navigate(`/${getActiveBrandConfig()}/tableau-de-bord/cours`, {
                                                state: 3,
                                            });
                                        }}
                                    />
                                </div>
                            </HomeHeader>
                        }
                    >
                        {!isLoading && <PartnerAdviceBanner variant="full" className="mb-4" />}
                        {activeTab === "start" && (
                            <OnboardingTab
                                teachr={teachr}
                                totalStudents={totalStudents}
                                brandColors={brandColors}
                                primaryTextClass={classes.primaryText}
                                steps={onboardingSteps}
                                overallProgress={onboardingProgress}
                                currentLevel={currentLevelInfo}
                                currentLevelNum={currentLevelNum}
                                // nextLevel et onNext - réintégrer à la prochaine version
                                isLoading={isLoading}
                            />
                        )}
                        {activeTab === "all-bord" && (
                            <AllBordTab
                                teachr={teachr}
                                totalStudents={totalStudents}
                                totalStudentsNoActive={totalStudentsNoActive}
                                upcomingCourses={upcomingCourses}
                                dataChart={infoChartData}
                                amountAndDurationData={amountAndDurationData}
                                course={course}
                                brandColors={brandColors}
                                primaryTextClass={classes.primaryText}
                                isLoading={isLoading}
                                onDeclare={handleDeclare}
                                onAddStudent={handleAddUser}
                                onDrafts={() => navigate(draftsPath())}
                                draftsCount={Array.isArray(coursesNext) ? coursesNext.reduce((n: number, g: any) => n + (g?.courses?.length ?? 0), 0) : 0}
                                sponsorshipMetrics={sponsorshipMetrics}
                                sponsorshipCards={sponsorshipCards}
                                onNavigateToSponsorship={handleNavigateToSponsorship}
                            />
                        )}
                    </Layout>
                }
                teachr={teachr}
            />
        </DashboardProvider>
    );
};

export default TeachrHome;
